import { Fragment, type ElementType, type ReactNode } from "react";
import MarkdownIt, { type Token } from "markdown-it";

// `linkify: true` autolinks bare URLs (WhatsApp-style tappable text, on top
// of — not instead of — the separate LinkPreviewCard below the message).
// `breaks: true` treats a single newline as a line break rather than
// requiring a blank line for a new paragraph, since chat messages are
// typed, not authored as a document. `html: false` (markdown-it's default,
// kept explicit) — message content is arbitrary user input; never parse it
// as raw HTML (this is what makes rendering it directly safe from XSS).
// Mirrors mobile/src/lib/markdown.tsx's identical renderer, same parser
// options, same token-walking approach — kept in sync by hand per this
// repo's "no shared package between clients" convention.
const md = new MarkdownIt({ linkify: true, breaks: true, html: false });

/** Renders a run of INLINE tokens (the `.children` of an `inline` block
 * token) as nested `<span>`s. */
function renderInline(tokens: Token[], keyPrefix: string): ReactNode {
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    const key = `${keyPrefix}-${i}`;
    switch (t.type) {
      case "text":
        nodes.push(<Fragment key={key}>{t.content}</Fragment>);
        break;
      case "softbreak":
      case "hardbreak":
        nodes.push(<br key={key} />);
        break;
      case "code_inline":
        nodes.push(
          <code key={key} className="rounded bg-black/30 px-1 font-mono text-[0.9em]">
            {t.content}
          </code>
        );
        break;
      case "strong_open": {
        const [inner, next] = consumeUntil(tokens, i, "strong_close");
        nodes.push(<strong key={key}>{renderInline(inner, key)}</strong>);
        i = next;
        continue;
      }
      case "em_open": {
        const [inner, next] = consumeUntil(tokens, i, "em_close");
        nodes.push(<em key={key}>{renderInline(inner, key)}</em>);
        i = next;
        continue;
      }
      case "s_open": {
        const [inner, next] = consumeUntil(tokens, i, "s_close");
        nodes.push(<s key={key}>{renderInline(inner, key)}</s>);
        i = next;
        continue;
      }
      case "link_open": {
        const href = String(t.attrGet("href") ?? "");
        const [inner, next] = consumeUntil(tokens, i, "link_close");
        nodes.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-300 underline"
          >
            {renderInline(inner, key)}
          </a>
        );
        i = next;
        continue;
      }
      default:
        // Unhandled inline token (e.g. an image) — skip rather than crash;
        // attachments already have their own dedicated rendering path.
        break;
    }
    i++;
  }
  return nodes;
}

/** Slices `tokens` from just after `openIndex` up to (not including) the
 * matching `closeType`, returning that slice plus the index right after the
 * closing token — the standard way to consume a markdown-it `_open`/`_close`
 * pair out of its flat token array. */
function consumeUntil(tokens: Token[], openIndex: number, closeType: string): [Token[], number] {
  let depth = 1;
  let j = openIndex + 1;
  const openType = tokens[openIndex].type;
  while (j < tokens.length && depth > 0) {
    if (tokens[j].type === openType) depth++;
    else if (tokens[j].type === closeType) depth--;
    if (depth > 0) j++;
  }
  return [tokens.slice(openIndex + 1, j), j + 1];
}

const HEADING_CLASS: Record<string, string> = {
  h1: "text-lg font-bold",
  h2: "text-base font-bold",
  h3: "text-base font-bold",
  h4: "text-sm font-bold",
  h5: "text-sm font-bold",
  h6: "text-sm font-bold"
};

/** Renders the top-level (block) token array from `md.parse()`. Handles the
 * subset that actually shows up in short chat messages — paragraphs,
 * headings, bold/italic/strikethrough/code/links, bullet/ordered lists,
 * fenced/indented code blocks, horizontal rules, and blockquotes — and
 * silently skips anything else (tables, images, footnotes, …) rather than
 * crashing on a message that happens to contain that syntax. No syntax
 * highlighting for code fences (would need a highlighter dependency for
 * what's realistically a one-liner in a chat message) — just a monospace
 * block. */
function renderBlocks(tokens: Token[], keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let i = 0;
  let blockIndex = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    const key = `${keyPrefix}-b${blockIndex}`;
    if (t.type === "paragraph_open") {
      const inline = tokens[i + 1];
      // A "tight" list item's paragraph_open/close is marked `.hidden` —
      // render its content inline (no <p> margin) rather than as a block.
      const Tag = t.hidden ? Fragment : "p";
      nodes.push(<Tag key={key}>{inline.type === "inline" ? renderInline(inline.children ?? [], key) : null}</Tag>);
      i += 3; // open, inline, close
    } else if (t.type === "heading_open") {
      const inline = tokens[i + 1];
      const Tag = t.tag as ElementType;
      nodes.push(
        <Tag key={key} className={HEADING_CLASS[t.tag]}>
          {inline.type === "inline" ? renderInline(inline.children ?? [], key) : null}
        </Tag>
      );
      i += 3;
    } else if (t.type === "bullet_list_open" || t.type === "ordered_list_open") {
      const ordered = t.type === "ordered_list_open";
      const [items, next] = consumeUntil(tokens, i, ordered ? "ordered_list_close" : "bullet_list_close");
      const ListTag = ordered ? "ol" : "ul";
      const rows: ReactNode[] = [];
      let j = 0;
      while (j < items.length) {
        if (items[j].type === "list_item_open") {
          const [itemTokens, itemNext] = consumeUntil(items, j, "list_item_close");
          rows.push(<li key={`${key}-li-${j}`}>{renderBlocks(itemTokens, `${key}-li-${j}`)}</li>);
          j = itemNext;
        } else {
          j++;
        }
      }
      nodes.push(
        <ListTag key={key} className={ordered ? "list-decimal pl-5" : "list-disc pl-5"}>
          {rows}
        </ListTag>
      );
      i = next;
    } else if (t.type === "code_block" || t.type === "fence") {
      nodes.push(
        <pre key={key} className="my-1 overflow-x-auto rounded-md bg-black/30 px-2 py-1.5">
          <code className="font-mono text-[0.85em]">{t.content.replace(/\n$/, "")}</code>
        </pre>
      );
      i++;
    } else if (t.type === "hr") {
      nodes.push(<hr key={key} className="my-2 border-slate-600" />);
      i++;
    } else if (t.type === "blockquote_open") {
      const [inner, next] = consumeUntil(tokens, i, "blockquote_close");
      nodes.push(
        <blockquote key={key} className="my-1 border-l-2 border-slate-500 pl-2">
          {renderBlocks(inner, key)}
        </blockquote>
      );
      i = next;
    } else {
      // Hidden/unhandled tokens — skip one and move on.
      i++;
    }
    blockIndex++;
  }
  return nodes;
}

/** Renders a message's text as Markdown. Safe by construction — `html:
 * false` means markdown-it never produces raw-HTML tokens from message
 * content, and every element here comes from this file's own fixed set of
 * React elements, not from interpolating message text into JSX/innerHTML. */
export function renderMarkdown(source: string): ReactNode {
  const tokens = md.parse(source, {});
  return <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">{renderBlocks(tokens, "md")}</div>;
}
